import React, { KeyboardEvent, useMemo, useState } from "react";
import { Box, Chip, TextField } from "@mui/material";

export interface SnChipInputItem {
  label: string;
  value?: string;
}

interface SnChipInputProps {
  label?: string;
  placeholder?: string;
  value: SnChipInputItem[];
  onChange: (value: SnChipInputItem[]) => void;
  disabled?: boolean;
  maxItems?: number;
}

const normalizeText = (text: string) => text.trim();

/**
 * 목적: 텍스트 입력값을 Chip 목록으로 관리하는 공통 입력 컴포넌트.
 *
 * 호출 예:
 * <SnChipInput
 *   value={participants}
 *   onChange={setParticipants}
 *   placeholder="Enter, Comma 입력"
 * />
 *
 * 주요 기능:
 * Enter 또는 Comma 입력 시 Chip 추가, Backspace 입력 시 마지막 Chip 삭제,
 * 중복 값과 빈 값을 자동 제외한다.
 *
 * 사용처:
 * 참석자, 태그처럼 여러 값을 JSON 배열로 저장해야 하는 화면 입력.
 *
 * @param value 현재 Chip 목록
 * @param onChange Chip 목록 변경 이벤트
 * @param label 입력값이 비어 있을 때 표시할 라벨
 * @param placeholder 입력 안내 문구
 * @param disabled 입력 비활성화 여부
 * @param maxItems 최대 Chip 개수
 */
const SnChipInput = ({
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  maxItems,
}: SnChipInputProps) => {
  const [inputValue, setInputValue] = useState("");

  const valueKeys = useMemo(
    () => new Set(value.map((item) => normalizeText(item.label))),
    [value]
  );

  const addChip = (text: string) => {
    const labelText = normalizeText(text);
    if (!labelText) return;
    if (valueKeys.has(labelText)) return;
    if (maxItems && value.length >= maxItems) return;

    onChange([...value, { label: labelText, value: labelText }]);
    setInputValue("");
  };

  const removeChip = (index: number) => {
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addChip(inputValue);
    }

    if (event.key === "Backspace" && !inputValue && value.length > 0) {
      removeChip(value.length - 1);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "4px",
        minHeight: "32px",
        border: "1px solid #babfc7",
        padding: "3px 6px",
        backgroundColor: disabled ? "#f5f5f5" : "#fff",
      }}
    >
      {value.map((item, index) => (
        <Chip
          key={`${item.label}-${index}`}
          label={item.label}
          size="small"
          disabled={disabled}
          onDelete={disabled ? undefined : () => removeChip(index)}
          sx={{ borderRadius: "3px", height: "24px" }}
        />
      ))}
      <TextField
        variant="standard"
        value={inputValue}
        label={value.length === 0 ? label : undefined}
        placeholder={placeholder}
        disabled={disabled || Boolean(maxItems && value.length >= maxItems)}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addChip(inputValue)}
        InputProps={{ disableUnderline: true }}
        sx={{
          flex: "1 1 120px",
          minWidth: "120px",
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "2px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "13px",
            transform: "translate(2px, 3px) scale(1)",
          },
        }}
      />
    </Box>
  );
};

export default React.memo(SnChipInput);
